import type { TagStatus } from '@indeed/ifl-components'
import {
  humanizeWireValue,
  lineStateLabels,
  lineStateTagStatuses,
} from 'syft-acp-core/components/SchedulingPlanDialog'
import type {
  AgencyJobAllocationStatus,
  AgencyJobHistoryEntry,
} from 'syft-acp-core/store/agency-jobs-history/types'

type StatusTag = { label: string; tagStatus: TagStatus }

// A status added to flex-core later falls back to a readable form of the raw value.
const knownStatuses: Record<AgencyJobAllocationStatus, StatusTag> = {
  active: { label: 'Active', tagStatus: 'info' },
  action_needed: { label: 'Action needed', tagStatus: 'warning' },
  partially_cascaded: { label: 'Partially cascaded', tagStatus: 'warning' },
  cascaded: { label: 'Cascaded', tagStatus: 'warning' },
  // Worded and coloured as the plan line the client's removal leaves, so the card and the plan agree.
  partially_removed_by_client: {
    label: 'Partially removed by client',
    tagStatus: lineStateTagStatuses.removed_by_client,
  },
  removed_by_client: {
    label: lineStateLabels.removed_by_client,
    tagStatus: lineStateTagStatuses.removed_by_client,
  },
  removed: { label: 'Removed', tagStatus: 'neutral' },
  declined: { label: 'Declined', tagStatus: 'neutral' },
}

export const getAgencyJobStatusTag = (status: string): StatusTag =>
  (knownStatuses as Record<string, StatusTag>)[status] ?? {
    label: humanizeWireValue(status),
    tagStatus: 'neutral',
  }

/** Live means neither declined nor ended, read from the timestamps rather than the status. */
export const isLiveAgencyJob = (agencyJob: AgencyJobHistoryEntry) =>
  !agencyJob.declined_at && !agencyJob.ended_at

const clientRemovalStatuses: ReadonlyArray<AgencyJobAllocationStatus> = [
  'partially_removed_by_client',
  'removed_by_client',
]

/**
 * The statuses shown as the agency job's badges, highest precedence first. Falls back to `status` when
 * `statuses` is absent, as from a flex-core that predates it, or empty. flex-core sends only the client's
 * removals when nothing else happened to a live agency job, so Active is added after them to say the
 * agency still holds it.
 */
export const getAgencyJobStatuses = (
  agencyJob: AgencyJobHistoryEntry,
): ReadonlyArray<AgencyJobAllocationStatus> => {
  const statuses =
    agencyJob.statuses && agencyJob.statuses.length > 0 ? agencyJob.statuses : [agencyJob.status]
  const isClientRemovalOnly = statuses.every(status => clientRemovalStatuses.includes(status))

  return isLiveAgencyJob(agencyJob) && isClientRemovalOnly ? [...statuses, 'active'] : statuses
}

export const formatShiftCount = (count: number) => `${count} shift${count === 1 ? '' : 's'}`

export const formatAgencyRank = (rank: number | null) => (rank === null ? 'Unranked' : `Rank ${rank}`)
